import { memo, type PropsWithChildren, type ReactNode, useState } from 'react';

import { Popover as AntPopover } from 'antd';
import type { PopoverProps as AntPopoverProps } from 'antd/es/popover';

export interface PopoverProps extends PropsWithChildren {
    trigger: ReactNode;
    content: ReactNode;
    placement?: AntPopoverProps['placement'];
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    className?: string;
    overlayClassName?: string;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

export const Popover = memo(
    ({
        placement = 'bottomLeft',
        trigger,
        content,
        open,
        onOpenChange,
        overlayClassName,
        getPopupContainer,
    }: PopoverProps) => {
        const [isOpenInternal, setIsOpenInternal] = useState(false);

        const isControlled = open !== undefined;
        const isOpen = isControlled ? open : isOpenInternal;

        const handleOpenChange = (newOpen: boolean) => {
            if (!isControlled) {
                setIsOpenInternal(newOpen);
            }

            onOpenChange?.(newOpen);
        };

        const popoverProps: AntPopoverProps = {
            content,
            onOpenChange: handleOpenChange,
            open: isOpen,
            placement,
            trigger: 'click',
            zIndex: 11000,
            arrow: false,
        };

        if (overlayClassName) {
            popoverProps.overlayClassName = overlayClassName;
        }

        if (getPopupContainer) {
            popoverProps.getPopupContainer = getPopupContainer;
        }

        return <AntPopover {...popoverProps}>{trigger}</AntPopover>;
    },
);

Popover.displayName = 'Popover';
